import {getBaseStickyHeaderHeight, observeStickyHeaderHeight, unobserveStickyHeaderHeight} from '@github-ui/sticky-css'
import {useCallback, useRef, useSyncExternalStore} from 'react'

function subscribeToStickyHeaderHeight(notify: () => void) {
  observeStickyHeaderHeight(notify)
  return () => unobserveStickyHeaderHeight(notify)
}

function useElementPosition(element: HTMLElement | null) {
  const sizesCacheRef = useRef<number | null>(null)

  const subscribeToResize = useCallback(
    (notify: () => void) => {
      if (!element) return () => undefined

      // Perform initial read and cache it immediately on subscription
      sizesCacheRef.current = element.getBoundingClientRect().bottom

      const observer = new ResizeObserver(() => {
        // Update cache when ResizeObserver fires, batched by browser
        sizesCacheRef.current = element.getBoundingClientRect().bottom
        notify()
      })
      observer.observe(element)
      return () => {
        observer.unobserve(element)
        observer.disconnect()
      }
    },
    [element],
  )

  // getSnapshot reads from cache, but needs to handle initial render before subscription
  const getSnapshot = useCallback(() => {
    if (!element) return 0
    // On first call before subscription, read and cache the value
    if (sizesCacheRef.current === null) {
      sizesCacheRef.current = element.getBoundingClientRect().bottom
    }
    return sizesCacheRef.current
  }, [element])

  return useSyncExternalStore(subscribeToResize, getSnapshot)
}

// Wrapper function that takes a CSS selector and uses useElementPosition
function useSelectorBottomPosition(selector: string) {
  const element = document.querySelector<HTMLElement>(selector)
  return useElementPosition(element)
}

/**
 * Height of the sticky site header, used to offset the issue side panel so it starts below the
 * global `.AppHeader` (and any notification shelf) rather than overlapping it.
 *
 * Unlike the issues dashboard — where the panel can sit in front of a full-page issue whose own
 * in-page sticky header must be cleared — the dashboard panel never has a full-page issue behind
 * it, so only the global header matters here. When `.AppHeader` isn't present, no offset applies.
 */
export const useSiteHeaderHeight = () => {
  const appHeaderBottom = useSelectorBottomPosition('.AppHeader')
  // Showing or dismissing the notification shelf only moves the header, which a ResizeObserver
  // can't see, so subscribe to the shelf's own height store to keep the offset current.
  const notificationShelfHeight = useSyncExternalStore(
    subscribeToStickyHeaderHeight,
    getBaseStickyHeaderHeight,
    getBaseStickyHeaderHeight,
  )

  return appHeaderBottom > 0 ? appHeaderBottom + notificationShelfHeight : 0
}
